import React from 'react';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import type { UnitPriceType } from 'shared/src/domain/line-item/line-item-channels';
import { getUnitName } from 'shared/src/domain/line-item/line-item-utils';
import { useCubeQuery } from '@/components/display/use-cube-query';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import { MetricSkeleton } from '@/components/feedback/metric-skeleton';
import { ActivePacingMetric, ExpiredPacingMetric, PendingPacingMetric } from './pacing-metrics';
import { getDateRangeState, getEffectiveDateRange } from './metric-date-utils';

type UnitsPacingMetricProps = {
  dateRange: ISODateRange;
};

export function UnitsPacingMetric({ dateRange }: UnitsPacingMetricProps) {
  const { lineItem } = useLineItemContext();
  const state = getDateRangeState(dateRange);
  const [effectiveFrom, effectiveTo] = getEffectiveDateRange(dateRange, state);

  const result = useCubeQuery({
    measures: [
      'line_item_metrics_daily.delivery_pacing',
      'line_item_metrics_daily.actual_units',
      'line_item_metrics_daily.planned_units'
    ],
    dimensions: ['line_items.unit_price_type_name'],
    timeDimensions: [
      {
        dimension: 'line_item_metrics_daily.day',
        dateRange: [effectiveFrom, effectiveTo]
      }
    ],
    filters: [
      {
        member: 'line_item_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItem.id]
      }
    ]
  });

  if (result.status === 'loading') return <MetricSkeleton />;
  if (result.status === 'error') return null;

  if (state === 'pending' || result.rows.length === 0) {
    return <PendingPacingMetric title="Delivery" detail={`0 / 0 on-pace`} />;
  }

  const {
    'line_items.unit_price_type_name': unitPriceTypeName,
    'line_item_metrics_daily.delivery_pacing': pacing,
    'line_item_metrics_daily.actual_units': actualUnits,
    'line_item_metrics_daily.planned_units': plannedUnits
  } = result.formattedRows[0];
  const unitAbbr = getUnitAbbr(unitPriceTypeName);

  if (state === 'active') {
    return (
      <ActivePacingMetric
        title="Delivery"
        pacing={pacing}
        detail={`${actualUnits} ${unitAbbr} / ${plannedUnits} on-pace`}
      />
    );
  }

  return (
    <ExpiredPacingMetric
      title="Delivery"
      pacing={pacing}
      detail={`${actualUnits} ${unitAbbr} of ${plannedUnits} planned`}
    />
  );
}

function getUnitAbbr(unitPriceTypeName: unknown): string {
  const name = unitPriceTypeName as UnitPriceType['name'];
  const { abbr } = getUnitName(name);
  return abbr || 'units';
}
